/**
 * Axios client.
 *
 * Mirrors the BenchTime convention: the API speaks snake_case, the UI speaks
 * camelCase, and the boundary is these two interceptors. Keeping it here means
 * no component ever has to think about casing.
 */
import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { requestCompanyId } from '../lib/companyScope';

// Augmenting the config type rather than casting at each call site: an
// opt-out that only exists as a cast is one a future caller cannot find.
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Do not narrow this request to the owner's acting company. */
    skipActingCompany?: boolean;
  }
}

const ACCESS_KEY = 'ats_access_token';
const REFRESH_KEY = 'ats_refresh_token';

export const tokenStore = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    // A view cannot outlive the session that opened it.
    viewAsToken = null;
  },
};

/**
 * Which company an owner is acting as, pushed here by the auth store.
 *
 * Held in this module rather than read from the store because the store
 * imports this file — reaching back the other way would be a cycle. A super
 * admin belongs to no company, so without this every company-scoped write is
 * refused with "super admin must specify a company_id"; setting it once here
 * is what saves a selector on ten separate forms.
 *
 * May hold ALL_COMPANIES, which is a choice this application understands and
 * the API does not. requestCompanyId is what keeps it off the wire.
 */
let actingCompanyId: string | null = null;

/**
 * A read-only token for somebody else's screens, when one is held.
 *
 * Sent instead of the caller's own. Theirs stays in localStorage untouched and
 * valid, so exiting the view is a matter of forgetting this one rather than
 * signing back in.
 */
let viewAsToken: string | null = null;

export function setViewAsToken(token: string | null): void {
  viewAsToken = token;
}

export function isViewingAsSomeoneElse(): boolean {
  return viewAsToken !== null;
}

export function setActingCompanyForRequests(companyId: string | null): void {
  actingCompanyId = companyId;
}

const snake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const camel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

/**
 * Fields whose value is a map keyed by *data*, not by field name.
 *
 * Everything else here is a schema: `full_name` becomes `fullName` because
 * both sides agreed on that name in advance. A custom field's values are not —
 * they are keyed by field id, and an id is content that happens to be sitting
 * in a key position.
 *
 * The damage is asymmetric, which is what makes it easy to miss. Going out,
 * `snake('cf_a1b2c3')` finds no capitals and changes nothing, so writes appear
 * to work. Coming back, `camel('cf_a1b2c3')` matches `_a` and returns
 * `cfA1b2c3` — so a value is saved under one key and read under another, and
 * the form renders empty with no error anywhere.
 *
 * Listed under both spellings because this runs in both directions.
 */
const OPAQUE_KEYED = new Set(['custom_values', 'customValues']);

function convertKeys(value: unknown, transform: (key: string) => string): unknown {
  if (Array.isArray(value)) return value.map((v) => convertKeys(v, transform));
  // Date and File must pass through untouched — spreading them destroys them.
  if (value instanceof Date || value instanceof File || value instanceof Blob) return value;
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        transform(k),
        // The key itself is still converted; only what hangs under it is
        // spared, because those keys are ids rather than field names.
        OPAQUE_KEYED.has(k) ? v : convertKeys(v, transform),
      ]),
    );
  }
  return value;
}

export const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  // Correct for every JSON call, and a trap for the few that are not: axios
  // only generates a multipart Content-Type *and its boundary* when the header
  // is unset, so this default silently suppresses it for FormData. The
  // interceptor below clears it again for those. Do not remove that without
  // removing this.
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = viewAsToken ?? tokenStore.access();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Applied before the casing conversion below, so it arrives as company_id.
  // An explicit value always wins: a caller that named a company meant it.
  // skipActingCompany exists for the owner's cross-company screens, where
  // narrowing to one company is precisely the wrong answer.
  const params = (config.params ?? {}) as Record<string, unknown>;
  const scopeTo = requestCompanyId(actingCompanyId);
  if (
    scopeTo
    && !config.skipActingCompany
    && params.companyId === undefined
    && params.company_id === undefined
  ) {
    config.params = { ...params, companyId: scopeTo };
  }

  if (config.data instanceof FormData) {
    // A file upload. Two things must not happen to it.
    //
    // Its keys are left alone — the caller writes them in snake_case by hand,
    // because converting a FormData means rebuilding it and File entries do
    // not survive that.
    //
    // And the instance's application/json default is removed, so axios
    // generates the multipart type and, critically, the boundary. Left in
    // place the body goes out labelled JSON with no boundary, Starlette
    // declines to parse it as a form, and FastAPI answers 422 "field
    // required" for a file that was in fact attached — an error describing
    // the opposite of what happened. Documents shipped with that bug and
    // Imports nearly did; fixing it per-caller only arms the trap for the
    // next one.
    config.headers.delete('Content-Type');
  } else if (config.data) {
    config.data = convertKeys(config.data, snake);
  }
  if (config.params) {
    config.params = convertKeys(config.params, snake);
  }
  return config;
});

/**
 * Refresh-on-401, with a single-flight guard.
 *
 * Without the guard, a page issuing six parallel queries on an expired token
 * would fire six refreshes. Because refresh tokens rotate, five of those would
 * be treated as replay and revoke the whole session — logging the user out for
 * doing nothing wrong.
 */
let refreshing: Promise<string | null> | null = null;

/** A refresh failed. Should the session be thrown away, or was it a blip?
 *
 * Only the server saying "this token is no longer valid" means signed out.
 * Everything else — the API restarting mid-deploy, a dropped connection, a
 * 502/503 from the front door, the login rate limiter — is transient, and
 * clearing the session for it logs people out for doing nothing wrong. That
 * was the single largest cause of the random logouts: the access token lives
 * 30 minutes, so every user refreshes all day, and every deploy restarts the
 * API underneath them.
 */
function isSessionOver(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  return err.response?.status === 401;
}

/** One refresh at a time across ALL tabs, not just this one.
 *
 * Refresh tokens rotate, and the server treats reuse as theft: it revokes every
 * session for that user. Two tabs each holding the same token is enough to
 * trigger it, so opening the app twice logged you out of both. The lock makes
 * the tabs take turns; the loser re-reads the token the winner stored.
 */
const REFRESH_LOCK = 'ats_refresh_lock';

async function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return run();                       // older browsers: single-flight only
  return locks.request(REFRESH_LOCK, run) as Promise<T>;
}

async function refreshAccessToken(): Promise<string | null> {
  const before = tokenStore.refresh();
  if (!before) return null;

  return withRefreshLock(async () => {
    // Another tab may have rotated while we waited for the lock. Its result is
    // already in storage, so use that rather than replaying a spent token.
    const current = tokenStore.refresh();
    if (!current) return null;
    if (current !== before) return tokenStore.access();

    try {
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/auth/refresh`,
        { refresh_token: current },
        { headers: { 'Content-Type': 'application/json' } },
      );
      tokenStore.set(data.access_token, data.refresh_token);
      return data.access_token as string;
    } catch (err) {
      // Keep the tokens on a transient failure; the next request retries.
      if (isSessionOver(err)) {
        // Record WHY before the error is discarded. The server explains this
        // 401 — "the account signed in elsewhere", or a token replay that
        // revoked everything — and this catch was throwing that sentence away,
        // leaving redirectToLogin() to send people to a bare form with nothing
        // to read. That silence is the whole of the "it signs me out by
        // itself" complaint.
        //
        // Only the tagged reasons are surfaced. An ordinary expiry also lands
        // here, and a banner on every timeout would train people to ignore the
        // one that matters.
        rememberTaggedSignOut(err);
        tokenStore.clear();
      }
      return null;
    }
  });
}

/** Header the API sets to say *why* a 401 happened. */
const AUTH_ERROR_HEADER = 'x-auth-error';
/** This account signed in somewhere else and that login won. */
const AUTH_ERROR_SUPERSEDED = 'session_superseded';
/** Every session was revoked because a sign-in token was presented twice. */
const AUTH_ERROR_REVOKED = 'session_revoked';

/** The 401s worth explaining. Anything else is an ordinary expiry. */
const EXPLAINED_SIGN_OUTS: readonly string[] = [
  AUTH_ERROR_SUPERSEDED,
  AUTH_ERROR_REVOKED,
];

/**
 * Where the reason for a forced sign-out waits for the login screen.
 *
 * sessionStorage rather than router state because getting there is a hard
 * navigation — the interceptor can be anywhere in the app, including inside a
 * background refetch, and there is no router in scope to carry state through.
 * Per-tab and cleared on read, so the message appears once, on the tab it
 * happened to, and never greets somebody who simply visited /login later.
 */
const SIGN_OUT_REASON_KEY = 'ats_signed_out_reason';

export function takeSignOutReason(): string | null {
  const reason = sessionStorage.getItem(SIGN_OUT_REASON_KEY);
  if (reason) sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
  return reason;
}

/**
 * Say why the next visit to the login screen happened.
 *
 * Exported for the deliberate sign-outs — changing a password ends every
 * session including this one, and landing on a bare login form after saving
 * reads as the save having failed rather than having worked.
 */
export function setSignOutReason(reason: string): void {
  sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
}

/**
 * Keep the server's explanation for a forced sign-out, if it gave one.
 *
 * Called from the refresh helper, which is the one place that sees these 401s
 * and the one place that used to discard them. The header is what separates a
 * reason worth showing from an ordinary expiry — the prose alone cannot, since
 * every one of these is a 401 carrying some sentence or other.
 */
function rememberTaggedSignOut(err: unknown): void {
  if (!axios.isAxiosError(err)) return;
  const code = err.response?.headers?.[AUTH_ERROR_HEADER];
  if (typeof code !== 'string' || !EXPLAINED_SIGN_OUTS.includes(code)) return;
  setSignOutReason(
    errorMessage(err, 'You were signed out. Please sign in again.'),
  );
}

function redirectToLogin(reason?: string): void {
  tokenStore.clear();
  if (reason) sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
  // Respect the configured base path so a sub-path deployment does not
  // redirect out of its own mount point.
  window.location.href = `${import.meta.env.BASE_URL || '/'}login`.replace('//', '/');
}

/**
 * Statuses worth trying once more, and nothing else.
 *
 * 502 is a gateway with no upstream to talk to; 503 is one refusing for now;
 * 504 is one that answered too slowly. All three are the *infrastructure*
 * failing rather than the request being wrong, so the same request sent a
 * moment later has a real chance of succeeding. A 4xx would not.
 */
const TRANSIENT_STATUSES = [502, 503, 504];

/** How long to wait before the one retry. Long enough for a container to
 *  finish coming up, short enough that nobody reads it as a hang. */
const RETRY_AFTER_MS = 1200;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether this request can safely be sent twice.
 *
 * GET and HEAD only. Retrying a POST would be how one logged call becomes
 * two, or one invoice becomes two -- the first request may well have reached
 * the API and succeeded, with only the *response* lost, and from here those
 * two cases are indistinguishable. A duplicate call row is a worse outcome
 * than an error message, so writes are left to the person to repeat.
 */
function isReplayable(config: InternalAxiosRequestConfig | undefined): boolean {
  const method = (config?.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head';
}

client.interceptors.response.use(
  (response) => {
    response.data = convertKeys(response.data, camel);
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean;
      _transientRetried?: boolean;
    };

    /**
     * One retry for a transient gateway failure.
     *
     * Staff were being shown "The server is restarting" several times a day
     * on a stack nobody was deploying. Whatever the cause turns out to be --
     * a container recreated underneath us, a moment of memory pressure, an
     * upstream briefly absent -- the window is seconds, and the request that
     * failed would have succeeded on the next attempt. Retrying it once is
     * the difference between an error somebody reports and a page that takes
     * a second longer to load.
     *
     * Deliberately once, not a loop with backoff. A genuine outage should
     * still surface as an error rather than as a screen that hangs politely
     * for thirty seconds, and a retry storm from every open tab is the last
     * thing a server under pressure needs.
     *
     * Separate flag from _retried, which the 401 refresh path owns: a request
     * may legitimately be retried once for a token and once for a 502, and
     * sharing one flag would let the first silently consume the second.
     */
    const status = error.response?.status;
    const transient = status !== undefined && TRANSIENT_STATUSES.includes(status);
    if (
      (transient || error.code === 'ERR_NETWORK')
      && original
      && !original._transientRetried
      && isReplayable(original)
    ) {
      original._transientRetried = true;
      await sleep(RETRY_AFTER_MS);
      return client(original);
    }

    const isAuthCall = original?.url?.includes('/auth/login')
      || original?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && original && !isAuthCall) {
      /**
       * Superseded is terminal, so it skips the refresh entirely.
       *
       * Not an optimisation. This account has signed in on another machine,
       * which revoked every refresh token it had — so the refresh below is
       * guaranteed to fail, and the only thing the round trip achieves is
       * replacing a 401 that explains itself with one that does not. Worse, by
       * the time the refresh has failed the original response is gone and with
       * it the only statement of why any of this happened.
       */
      /**
       * A 401 while viewing somebody else ends the view, always.
       *
       * Before the refresh below, and before the superseded branch, because
       * the refresh is the dangerous path: it renews the *viewer's* own
       * session — their refresh token is the only one in storage — and then
       * retries the request with it. The screen would keep working, the bar
       * would still say "Viewing as Priya", and every request from that point
       * would run as the admin with the admin's rights. A view that silently
       * turns into acting as yourself on somebody else's screen is precisely
       * what read-only exists to prevent.
       *
       * Two reasons, two messages: their sign-in killed the token, or it
       * simply ran out. Neither signs the viewer out — their own session was
       * never touched.
       */
      if (viewAsToken) {
        const superseded =
          error.response.headers?.[AUTH_ERROR_HEADER] === AUTH_ERROR_SUPERSEDED;
        const store = await import('../store/impersonation')
          .then((m) => m.useImpersonation.getState());
        if (superseded) store.endedByTargetSignIn();
        else store.endedByExpiry();
        return Promise.reject(error);
      }

      if (error.response.headers?.[AUTH_ERROR_HEADER] === AUTH_ERROR_SUPERSEDED) {
        redirectToLogin(
          'You were signed out because this account signed in on another '
          + 'machine. Only one machine can be signed in at a time.',
        );
        return Promise.reject(error);
      }

      if (!original._retried) {
        original._retried = true;
        refreshing = refreshing ?? refreshAccessToken().finally(() => { refreshing = null; });
        const token = await refreshing;

        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return client(original);
        }

        // Only leave when the session is genuinely over. refreshAccessToken
        // clears the tokens on a 401 and keeps them on anything transient, so
        // this tells an ordinary expiry — which needs no message, and a banner
        // on every timeout would train people to ignore the one that matters —
        // apart from the API simply restarting underneath us.
        if (!tokenStore.refresh()) redirectToLogin();
      }
    }

    return Promise.reject(error);
  },
);

/** Turn a FastAPI error payload into something worth showing a user. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0] as { msg?: string; loc?: string[] } | undefined;
      if (first?.msg) {
        const field = first.loc?.slice(1).join('.');
        return field ? `${field}: ${first.msg}` : first.msg;
      }
    }
    // The rate limiter answers before the request reaches Python, so there is no
    // JSON detail to read — without this the sign-in screen showed a generic
    // failure for something the user can act on by waiting a moment.
    if (error.response?.status === 429) {
      return 'Too many sign-in attempts from this network. Wait a minute and try again.';
    }
    // Three different failures used to share one sentence, and the sentence
    // was a guess. "The server is restarting" was shown for a throttled
    // request, for a dead upstream and for a slow one alike -- so every
    // report of it began by investigating a restart that in two cases out of
    // three had not happened. Each of these is now separable from the others
    // by reading the screen, which is the whole point.
    if (error.response?.status === 503) {
      // nginx answers 503 for limit_req as well as for a stopped upstream,
      // and the user's action is the same either way: wait, then retry.
      return 'The server is busy. Try again in a moment.';
    }
    if (error.response?.status === 502) {
      return 'The server is not responding. It may be restarting — try again in a moment.';
    }
    if (error.response?.status === 504) {
      // Distinct from 502 on purpose: the upstream is alive and took too
      // long, which is what a large import looks like from another tab.
      return 'The server took too long to answer. It may be busy with a large job.';
    }
    if (error.code === 'ECONNABORTED') return 'The request timed out';
    if (error.code === 'ERR_NETWORK') return 'Cannot reach the server';
  }
  return fallback;
}
